import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useT } from '../lib/i18n';
import { fmtShortDay, fmtTime } from '../lib/time';
import { getToken } from '../lib/api';
import { alert, confirm, haptic } from '../lib/telegram';

/**
 * «Постоянные»: клиент ходит, а впереди пусто.
 *
 * A client the coach has marked «Постоянный» is somebody he expects to
 * see every week. When the last lesson on the books comes within the
 * threshold, that slot is about to be taken by somebody else — and the
 * coach finds out only when it is gone.
 *
 * In this slice the app only says so: a chip with the date, one filter,
 * and one line on the client with «Напомнить». Nothing is ever sent
 * without the coach reading it first and pressing the button.
 *
 * Deliberately not the amber «Пора написать»: that one is about a person
 * drifting away, this one is about a person who is still coming while
 * his calendar empties.
 */

const BASE = import.meta.env.VITE_API_URL ?? '';

export interface Runway {
  clientId: string;
  name: string;
  username: string | null;
  botStarted: boolean;
  lastAt: string | null;
  lastKind: 'private' | 'group' | null;
  lastPeople: number;
  days: number | null;
}

export async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    ...init,
    headers: { ...(init.body == null ? {} : { 'content-type': 'application/json' }),
      authorization: `Bearer ${getToken()}`, ...(init.headers ?? {}) },
  });
  if (!res.ok) throw new Error((await res.json().catch(() => ({})) as any).error ?? `http_${res.status}`);
  return res.status === 204 ? (undefined as T) : res.json();
}

export const fetchRegulars = () => api<{ days: number; clients: Runway[] }>('/api/coach/regulars');

/**
 * Settings: the message in the coach's own language, on a real regular
 * or on «Сергей» when there is none, with the default line beside it.
 */
export const nudgeSample = () =>
  api<NudgeParts & { defaultBody: string; custom: boolean; sample: boolean }>('/api/coach/regulars/preview');

/** «Запись до 21 окт.» — a date, not a countdown: it does not move daily. */
export function runwayChip(t: ReturnType<typeof useT>, r: Runway) {
  return r.lastAt ? t('co.reg.chip', { date: fmtShortDay(new Date(r.lastAt)) }) : t('co.reg.none');
}

const when = (iso: string) => `${fmtShortDay(new Date(iso))}, ${fmtTime(iso)}`;

/** Server refusals of «Напомнить», in words. */
export function nudgeError(t: ReturnType<typeof useT>, code: string, name: string) {
  if (code === 'no_bot') return t('co.reg.errNoBot', { name });
  // 'too_soon' не попадает сюда: на него спрашивают, а не отказывают.
  if (code === 'nothing_to_say' || code === 'not_regular') return t('co.reg.errNothing');
  return t('co.failed', { message: code });
}

export interface NudgeParts {
  greeting: string; named: boolean; line: string; body: string;
  button: string; signoff: string; day: string | null;
}

/**
 * The message as Telegram will draw it: one bubble, the coach's sign-off
 * at the end, the booking button under it. No chat backdrop around it —
 * a frame inside a card inside a screen reads as three things.
 *
 * The lines the app writes are grey and not touchable; the one line the
 * coach owns is either plain text (here) or an inline field (in
 * Settings), and it is the only thing in the bubble that looks editable.
 */
export function NudgeBubble({ parts, children }: { parts: NudgeParts; children?: ReactNode }) {
  return (
    <div className="tgmsg__bubble">
      <div className="tgmsg__fixed">{parts.greeting}</div>
      <div className="tgmsg__fixed">{parts.line}</div>
      {children ?? <div className="tgmsg__own">{parts.body}</div>}
      <div className="tgmsg__fixed tgmsg__sign">{parts.signoff}</div>
      <div className="tgmsg__btn">{parts.button}</div>
    </div>
  );
}

/**
 * «Напомнить»: the message itself, before it goes.
 *
 * The coach is sending something under his own name, so he reads it
 * first — the greeting and the lesson line as the app will write them,
 * his own text underneath, and the button the client will see.
 */
export function NudgeSheet({ client, onClose, onSent }: {
  client: { clientId: string; name: string }; onClose: () => void; onSent: () => void;
}) {
  const t = useT();
  const [parts, setParts] = useState<NudgeParts | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api<NudgeParts>(`/api/coach/clients/${client.clientId}/nudge-preview`)
      .then(setParts)
      .catch((err) => setError(nudgeError(t, err.message, client.name)));
  }, [client.clientId]);

  async function send(force = false) {
    setBusy(true);
    setError(null);
    try {
      await api(`/api/coach/clients/${client.clientId}/nudge`, {
        method: 'POST', body: JSON.stringify({ force }),
      });
      haptic.success();
      onSent();
      onClose();
      await alert(t('co.reg.reminded'));
    } catch (err: any) {
      // Писали недавно: это не отказ, это вопрос — и ответ нужен явный.
      if (err.message === 'too_soon') {
        haptic.error();
        setBusy(false);
        if (await confirm(t('co.reg.againAsk', { name: client.name }))) await send(true);
        return;
      }
      haptic.error();
      setError(nudgeError(t, err.message, client.name));
      setBusy(false);
    }
  }

  return createPortal(
    <div className="sheet" onClick={() => { if (!busy) onClose(); }}>
      <div className="sheet__panel" onClick={(e) => e.stopPropagation()}>
        <div className="sheet__title">{t('co.reg.sendTitle')}</div>
        <div className="card__when">{t('co.reg.sendWho')}</div>
        {parts ? <NudgeBubble parts={parts} />
          : !error ? <div className="skeleton" style={{ marginTop: 12 }} /> : null}
        {error && <div className="warnline">{error}</div>}
        <div className="card__when" style={{ marginTop: 8 }}>{t('co.reg.sendHint')}</div>
        <div className="sheet__actions">
          <button className="paybar__opt" onClick={onClose}>{t('co.offer.cancel')}</button>
          <button className="savebtn" disabled={busy || !parts} onClick={() => send()}>{t('co.reg.send')}</button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
